// plan-modusign-style-acceptance §Phase2 — SignatureDocument API client.

import type {
  SignatureDocumentStatus,
  SignatureDocumentType,
  SignatureDocumentSummaryDto,
  SignatureDocumentDetailDto,
  SignatureSignatorySummaryDto,
  CreateSignatureDocumentDto,
  UpdateSignatureSignatoryDto,
  SignatureSignOrder,
} from '@bhops/shared';
import axios from 'axios';
import { apiClient, extractApiError } from './client';
import { downloadBlob } from './download';

export interface ListDocumentsQuery {
  status?: SignatureDocumentStatus;
  documentType?: SignatureDocumentType;
  // round6 §1-6(f) — 고객사 서류 탭 '서명 완료 문서' 섹션용 필터.
  customerId?: string;
  // data-lifecycle §3 — 삭제됨(soft-delete) 포함(서명 운영 역할 전용; 서버에서 확인).
  includeDeleted?: boolean;
}

export const signatureDocumentsApi = {
  async list(query: ListDocumentsQuery = {}): Promise<SignatureDocumentSummaryDto[]> {
    const { data } = await apiClient.get<SignatureDocumentSummaryDto[]>('/signature-documents', {
      params: query,
    });
    return data;
  },

  async get(id: string): Promise<SignatureDocumentDetailDto> {
    const { data } = await apiClient.get<SignatureDocumentDetailDto>(`/signature-documents/${id}`);
    return data;
  },

  async create(dto: CreateSignatureDocumentDto): Promise<{ id: string }> {
    const { data } = await apiClient.post<{ id: string }>('/signature-documents', dto);
    return data;
  },

  /**
   * 개선계획 §5-4 (결정 ⑥-1A) — 발송 시 고르는 첨부 PDF 의 presigned PUT.
   * 문서 생성 전에 올리고, 받은 s3Key 를 create 의 attachmentS3Key 로 보낸다.
   */
  async requestAttachmentUploadUrl(
    filename: string,
    byteSize: number,
  ): Promise<{ uploadUrl: string; s3Key: string; headers: Record<string, string> }> {
    const { data } = await apiClient.post<{
      uploadUrl: string;
      s3Key: string;
      headers: Record<string, string>;
    }>('/signature-documents/attachment-upload-url', { filename, byteSize });
    return data;
  },

  /**
   * presigned PUT 로 첨부 bytes 를 S3 에 직접 올린다. 서명된 헤더
   * (content-type / content-length)를 그대로 forward 해야 403 이 나지 않는다.
   * (signatureTemplates.uploadPdf 와 같은 규약.)
   */
  async uploadAttachment(
    uploadUrl: string,
    file: File,
    signedHeaders: Record<string, string>,
  ): Promise<void> {
    const headers = new Headers();
    for (const [k, v] of Object.entries(signedHeaders)) headers.set(k, v);
    let res: Response;
    try {
      res = await fetch(uploadUrl, { method: 'PUT', body: file, headers });
    } catch (err) {
      const reason = err instanceof Error ? err.message : 'network error';
      throw new Error(`첨부 파일을 올리지 못했습니다. 인터넷 연결을 확인해 주세요. (${reason})`);
    }
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      const snippet = text.slice(0, 240).replace(/\s+/g, ' ').trim();
      throw new Error(
        `첨부 파일을 올리지 못했습니다. 잠시 후 다시 시도해 주세요. (${res.status} ${res.statusText} ${snippet})`,
      );
    }
  },

  async cancel(id: string): Promise<void> {
    await apiClient.post(`/signature-documents/${id}/cancel`);
  },

  /**
   * sign-remote-link 트랙2(D3) — 서명자 이름·전화 편집(운영자 콘솔).
   * 문서 IN_PROGRESS + 해당 서명자 미서명(PENDING|OPENED)일 때만 200, 아니면 400.
   */
  async updateSignatory(
    documentId: string,
    signatoryId: string,
    dto: UpdateSignatureSignatoryDto,
  ): Promise<SignatureSignatorySummaryDto> {
    const { data } = await apiClient.patch<SignatureSignatorySummaryDto>(
      `/signature-documents/${documentId}/signatories/${signatoryId}`,
      dto,
    );
    return data;
  },
  /**
   * 로봇을 나중에 정하거나 바꾼다(2026-09-23). 진행 중 문서만 — 완료 문서는 서버가 400.
   * 첫 번째가 대표 로봇, 빈 배열이면 로봇 없음. 바뀐 문서 상세를 돌려준다.
   */
  async updateRobots(documentId: string, robotIds: string[]): Promise<SignatureDocumentDetailDto> {
    const { data } = await apiClient.patch<SignatureDocumentDetailDto>(
      `/signature-documents/${documentId}/robots`,
      { robotIds },
    );
    return data;
  },
  /** offline-signing PR-5 (plan D7) — finalize 안전망 수동 트리거. */
  async refinalize(id: string): Promise<{ finalPdfS3Key: string | null }> {
    const { data } = await apiClient.post<{ finalPdfS3Key: string | null }>(
      `/signature-documents/${id}/refinalize`,
    );
    return data;
  },

  /** Round 2 Phase G (#5, D-6) — soft-delete(deletedAt). 감사 트레일은 보존. */
  async remove(id: string): Promise<void> {
    await apiClient.delete(`/signature-documents/${id}`);
  },

  /** data-lifecycle plan §3 — soft-delete 복구(셀프). */
  async restore(id: string): Promise<void> {
    await apiClient.post(`/signature-documents/${encodeURIComponent(id)}/restore`);
  },

  /** data-lifecycle plan §3/D3·D4 — 셀프 완전삭제(소프트삭제된 문서만, FK 가드). */
  async hardDelete(id: string): Promise<void> {
    await apiClient.delete(`/signature-documents/${encodeURIComponent(id)}/permanent`);
  },

  /** Phase 3 — fetch presigned URL for the final stamped PDF. */
  async getPdfUrl(id: string): Promise<{ url: string }> {
    const { data } = await apiClient.get<{ url: string }>(`/signature-documents/${id}/pdf`);
    return data;
  },

  /**
   * sign-console-prefill-2026-07 — 운영자 '미리 채우기' 값 서버 저장(콘솔).
   * 서버가 prefillable(text/date/checkbox/dropdown) 필드 id 만 보존한다. 빈 맵을
   * 보내면 기존 저장분을 비운다. IN_PROGRESS 문서 + 서명 운영 역할만(서버 가드).
   */
  async savePrefill(id: string, fieldValues: Record<string, string>): Promise<void> {
    await apiClient.put(`/signature-documents/${id}/prefill`, { fieldValues });
  },

  /**
   * 추가수정 2차(26-07-27) §4 (결정 ⑤A) — "미리 채운 PDF 다운로드".
   * 진행 중 문서를 지금까지 채워진 값 그대로 **문서 전체 1개 PDF** 로 받는다
   * (액세서리 페이지 포함, 페이지 선택 단계 없음). 최종본과 같은 합성기라
   * 글자가 벡터로 찍힌다 — 화면 캡쳐(래스터)의 대체.
   *
   * 파일명 — 서버 컨트롤러는 한글 파일명을 Node 의 res.setHeader 가 ERR_INVALID_CHAR 로
   * 거부하는 문제 때문에 Content-Disposition 에 RFC 5987 `filename*=UTF-8''…` 만 싣는다.
   * 2026-10-09 부터 downloadBlob(api/download.ts filenameFromDisposition)이 `filename*` 도
   * 읽으므로, 헤더를 읽을 수 있으면 서버 이름이 쓰인다(CORS exposedHeaders 는 10/8 부터).
   * 그 전에는 이 경로에서 **항상 fallbackName** 이 쓰였다.
   *
   * ⚠ 그래도 fallbackName 은 서버와 같은 규칙의 같은 한글 이름("(작성중) 고객사_유형_YYMMDD.pdf")
   * 으로 넘길 것 — 헤더가 가려지는 환경에서는 여전히 이 이름이 저장 이름이 된다. 규칙을 바꿔야
   * 하면 서버 컨트롤러 주석과 이 fallback 을 함께 고칠 것.
   */
  async downloadPrefillPreview(id: string, fallbackName: string): Promise<void> {
    await downloadBlob(
      `/signature-documents/${encodeURIComponent(id)}/prefill-preview.pdf`,
      undefined,
      fallbackName,
    );
  },

  /**
   * 개선 9건 차수1(26-08-04) §4 (결정 ③A + 완료본도) — **페이지 선택 다운로드**의
   * 원본 bytes. 썸네일을 그리는 데 쓴다.
   *
   * ⚠ 썸네일을 **이 bytes 로만** 그린다. 상세 DTO 의 pdfDownloadUrl(진행 중이면
   * 베이스 양식 원본)로 그리면 첨부(액세서리)가 붙은 문서에서 페이지 번호가 어긋나
   * 엉뚱한 페이지를 내려받는다 — 다운로드가 뽑는 문서와 화면이 세는 문서가 **같은
   * 한 벌**이어야 한다. 그래서 미리보기와 다운로드가 같은 경로를 쓴다.
   */
  async fetchDocumentPdfBytes(id: string, variant: SignPdfVariant): Promise<ArrayBuffer> {
    try {
      const { data } = await apiClient.get<ArrayBuffer>(signPdfPath(id, variant), {
        responseType: 'arraybuffer',
      });
      return data;
    } catch (err) {
      throw new Error(decodeBinaryApiError(err));
    }
  },

  /**
   * 개선 9건 차수1(26-08-04) §4 — 고른 페이지만 PDF 로 저장. `pages` 는 1-기준
   * 페이지 번호 배열(예: [3,4]) — 서버가 실제 페이지 수와 대조하고, 문서에 없는
   * 페이지거나 0개면 400 을 돌려준다(조용히 전체를 주지 않는다).
   *
   * fallbackName 규칙 주의사항은 위 downloadPrefillPreview 주석과 동일하다
   * (서버는 `filename*` 만 싣는다 — 헤더를 못 읽는 환경에서는 fallbackName 이 저장 이름).
   */
  async downloadDocumentPages(
    id: string,
    variant: SignPdfVariant,
    pages: ReadonlyArray<number>,
    fallbackName: string,
  ): Promise<void> {
    await downloadBlob(signPdfPath(id, variant), { pages: pages.join(',') }, fallbackName);
  },
};

/**
 * 개선 9건 차수1(26-08-04) §4 — 어느 PDF 에서 페이지를 고르는가.
 *   'prefill' = 진행 중 문서의 미리 채운 합성본(서명칸 빈 상태)
 *   'final'   = 완료 문서의 최종 서명본(오너 결정 '완료본도')
 * 클라이언트가 **선언**하고 서버가 그 경로를 탄다 — 서버가 상태를 보고 짐작해
 * 다른 문서를 주는 구조를 만들지 않는다.
 */
export type SignPdfVariant = 'prefill' | 'final';

function signPdfPath(id: string, variant: SignPdfVariant): string {
  const encoded = encodeURIComponent(id);
  return variant === 'final'
    ? `/signature-documents/${encoded}/final.pdf`
    : `/signature-documents/${encoded}/prefill-preview.pdf`;
}

/**
 * 개선 9건 차수1(26-08-04) §4 — **바이너리 응답의 오류 본문을 되살린다.**
 *
 * `responseType: 'arraybuffer'` 로 부르면 axios 는 **오류 응답 본문까지** ArrayBuffer
 * 로 준다. 그래서 extractApiError 의 `body.detail` 경로가 통째로 비고, 서버가 애써
 * 한글로 적은 안내("최종 PDF 가 아직 없습니다 — [PDF 재생성] 을 먼저 눌러 주세요")가
 * 조용히 사라져 일반 문구만 뜬다. 여기서 본문을 JSON 으로 되살려 그 문장을 살린다.
 *
 * (근본 해결은 extractApiError 가 ArrayBuffer 본문을 알아보게 하는 것 — client.ts 는
 *  이 차수의 수정 범위 밖이라 호출부에서 봉합했다.)
 */
export function decodeBinaryApiError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const raw = err.response?.data;
    // ⚠ `raw instanceof ArrayBuffer` 만으로는 부족하다 — jsdom/Node 처럼 realm 이
    // 갈리면 같은 ArrayBuffer 도 instanceof 를 통과하지 못한다(시험에서 실제로 걸렸다).
    // 뷰(Uint8Array)와 버퍼 둘 다, 그리고 realm 무관하게 받는다.
    const view = ArrayBuffer.isView(raw)
      ? new Uint8Array(raw.buffer, raw.byteOffset, raw.byteLength)
      : isArrayBufferLike(raw)
        ? new Uint8Array(raw)
        : null;
    const text = view ? new TextDecoder().decode(view) : null;
    if (text) {
      try {
        const body = JSON.parse(text) as { detail?: string; title?: string; message?: string };
        const msg = body.detail ?? body.title ?? body.message;
        if (msg) return msg;
      } catch {
        // JSON 이 아니면(프록시 HTML 등) 아래 공통 처리로 넘긴다.
      }
    }
  }
  return extractApiError(err);
}

/** realm 무관 ArrayBuffer 판정(instanceof 는 jsdom/Node 경계에서 새어 나간다). */
function isArrayBufferLike(v: unknown): v is ArrayBuffer {
  return v instanceof ArrayBuffer || Object.prototype.toString.call(v) === '[object ArrayBuffer]';
}

export { SignatureSignOrder };
